import { useQuery } from 'react-query';
import { useAuth } from '@providers/auth';
import request from '@utils/axios-utils';
import { IncomeExpensesAnalyticsResponse, PaidDocumentsRatioResponse, CostCenterBreakdownResponse } from '@types';

interface IncomeExpensesAnalyticsParams {
  startMonth?: string;
  endMonth?: string;
  enabled?: boolean;
  companyIds?: string[];
  costCenterId?: string | null;
}

interface PaidDocumentsRatioParams {
  startMonth?: string;
  endMonth?: string;
  enabled?: boolean;
  companyIds?: string[];
  costCenterId?: string | null;
}

interface CostCenterBreakdownParams {
  startMonth?: string;
  endMonth?: string;
  enabled?: boolean;
  companyIds?: string[];
}

export function useIncomeExpensesAnalytics({
  startMonth,
  endMonth,
  enabled = true,
  companyIds,
  costCenterId,
}: IncomeExpensesAnalyticsParams) {
  const { session } = useAuth();

  return useQuery<IncomeExpensesAnalyticsResponse>(
    ['analytics', 'income-expenses', startMonth ?? null, endMonth ?? null, companyIds ?? [], costCenterId ?? null],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: '/analytics/income_expenses',
        method: 'GET',
        params: {
          ...(startMonth ? { start_month: startMonth } : {}),
          ...(endMonth ? { end_month: endMonth } : {}),
          ...(companyIds?.length ? { company_id: companyIds.join(',') } : {}),
          ...(costCenterId ? { cost_center_id: costCenterId } : {}),
        },
      });

      return response.data;
    },
    {
      enabled: session.status === 'loggedIn' && enabled,
    },
  );
}

export function usePaidDocumentsRatio({
  startMonth,
  endMonth,
  enabled = true,
  companyIds,
  costCenterId,
}: PaidDocumentsRatioParams) {
  const { session } = useAuth();

  return useQuery<PaidDocumentsRatioResponse>(
    ['analytics', 'paid-documents-ratio', startMonth ?? null, endMonth ?? null, companyIds ?? [], costCenterId ?? null],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: '/analytics/paid_documents_ratio',
        method: 'GET',
        params: {
          ...(startMonth ? { start_month: startMonth } : {}),
          ...(endMonth ? { end_month: endMonth } : {}),
          ...(companyIds?.length ? { company_id: companyIds.join(',') } : {}),
          ...(costCenterId ? { cost_center_id: costCenterId } : {}),
        },
      });

      return response.data;
    },
    {
      enabled: session.status === 'loggedIn' && enabled,
    },
  );
}

export function useCostCenterBreakdown({
  startMonth,
  endMonth,
  enabled = true,
  companyIds,
}: CostCenterBreakdownParams) {
  const { session } = useAuth();

  return useQuery<CostCenterBreakdownResponse>(
    ['analytics', 'cost-center-breakdown', startMonth ?? null, endMonth ?? null, companyIds ?? []],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: '/analytics/cost_center_breakdown',
        method: 'GET',
        params: {
          ...(startMonth ? { start_month: startMonth } : {}),
          ...(endMonth ? { end_month: endMonth } : {}),
          ...(companyIds?.length ? { company_id: companyIds.join(',') } : {}),
        },
      });

      return response.data;
    },
    {
      enabled: session.status === 'loggedIn' && enabled,
    },
  );
}
